import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Button, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { api } from 'src/api';
import { UtilityPortalCredentialSection } from 'src/components/property/UtilityPortalCredentials';
import { onCredentialsChanged } from 'src/components/property/UtilityPortalCredentials/credentialEvents';
import { useStore } from 'src/stores';
import { getCurrentCompanyId } from 'src/stores/billAutomation/currentCompanyId';
import { readSubscriptionTier } from 'src/stores/company/subscriptionTierRead';
import { NO_OPTIONS } from 'src/stores/utilitySetups/companySetupsRead';
import { canManageUtilityCredentials } from 'src/utils/canManageUtilityCredentials';
import { sameId } from './parts';
import useStyles from './styles';
const LOADING = {
  key: null,
  status: 'loading',
  portal: false,
  credential: null
};

/**
 * Whether Livable can fetch this utility's bills from its provider's portal, and with which login (PRD V3, FR-7):
 * fetchable means the provider has a scraper key, and a login is mapped while one that isn't disabled serves the utility,
 * as property-service checks before a utility joins a portal setup (LB-6184).
 */
export const portalCheckLine = (_ref, credential) => {
  let name = _ref.name,
    isFetchable = _ref.isFetchable;
  const provider = name || "This utility's provider";
  if (!isFetchable) {
    return "".concat(provider, " has no portal Livable can fetch from, so this utility's bills can't come from the portal. Link it to a setup with another way bills reach Livable.");
  }
  if (!credential) return "".concat(provider, " has a portal Livable can fetch from. Save the login to map it to this utility.");
  if (credential.disabledOn) {
    return "".concat(provider, " has a portal Livable can fetch from, but this utility's login is disabled, so Livable doesn't fetch with it.");
  }
  return "".concat(provider, " has a portal Livable can fetch from, and a login is mapped to this utility.");
};

/**
 * This utility's portal login, shown by the Setup section (LB-6191). On a plan without portal fetching only a login that
 * already exists shows, still editable: only a new login needs the plan. Reads go to the API directly, so a section per
 * utility on the page never cancels another's.
 */
export const PortalAccount = observer(_ref2 => {
  let propertyId = _ref2.propertyId,
    propertyUtilityId = _ref2.propertyUtilityId,
    utilityTypeId = _ref2.utilityTypeId,
    provider = _ref2.provider,
    className = _ref2.className;
  const classes = useStyles();
  const store = useStore();
  // Observed, so a company switch re-renders this and reads the new company, as the Setup section does.
  const companyAccessId = store.homeDashboardStore.companyAccessId;
  const companyId = getCurrentCompanyId();
  const canManage = canManageUtilityCredentials();
  const key = "".concat(companyId, "|").concat(propertyUtilityId);

  // 'loading' | 'planFailed' | 'failed' | 'ready', whose it is, whether the plan fetches portals, and the login.
  const _useState = useState(LOADING),
    _useState2 = _slicedToArray(_useState, 2),
    read = _useState2[0],
    setRead = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    attempt = _useState4[0],
    setAttempt = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    saves = _useState6[0],
    setSaves = _useState6[1];
  useEffect(() => onCredentialsChanged(() => setSaves(n => n + 1)), []);
  useEffect(() => {
    if (!companyId || !propertyUtilityId || !canManage) return undefined;
    // A switch, retry or save can overlap a read still out, and only this one may answer. A read again for the same
    // utility keeps what's shown until it answers, so a save's confirmation isn't lost to a loading line.
    let active = true;
    setRead(current => current.key === key ? current : _objectSpread(_objectSpread({}, LOADING), {}, {
      key
    }));
    (async _plan$allows => {
      const plan = await readSubscriptionTier(companyId);
      if (!active) return;
      if (!plan) {
        setRead(_objectSpread(_objectSpread({}, LOADING), {}, {
          key,
          status: 'planFailed'
        }));
        return;
      }
      const portal = !!((_plan$allows = plan.allows) !== null && _plan$allows !== void 0 && _plan$allows.portal);
      try {
        var _resp$response, _rows$find;
        const resp = await api.AdminApi.getUtilityCredentials({
          companyId,
          propertyId,
          propertyUtilityId
        }, NO_OPTIONS);
        if (!active) return;
        const rows = (_resp$response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response !== void 0 ? _resp$response : resp;
        if (!Array.isArray(rows)) throw new Error('No portal login answer');
        const credential = (_rows$find = rows.find(row => sameId(row === null || row === void 0 ? void 0 : row.propertyUtilityId, propertyUtilityId))) !== null && _rows$find !== void 0 ? _rows$find : null;
        setRead({
          key,
          status: 'ready',
          portal,
          credential
        });
      } catch (_unused) {
        if (active) setRead({
          key,
          status: 'failed',
          portal,
          credential: null
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId, companyAccessId, propertyId, propertyUtilityId, canManage, key, attempt, saves]);
  if (!companyId || !propertyUtilityId || !canManage || !provider) return null;

  // Until this utility's own answer arrives: a switch never shows another company's login.
  const _ref3 = read.key === key ? read : LOADING,
    status = _ref3.status,
    portal = _ref3.portal,
    credential = _ref3.credential;
  const rootClass = className ? "".concat(classes.portal, " ").concat(className) : classes.portal;
  const section = entitled => /*#__PURE__*/React.createElement(UtilityPortalCredentialSection, {
    companyId: companyId,
    utilityProviderId: provider.utilityProviderId,
    propertyId: propertyId,
    propertyUtilityId: propertyUtilityId,
    utilityTypeId: utilityTypeId,
    credential: credential,
    providerName: provider.name,
    isAutomated: !!provider.isFetchable,
    entitled: entitled,
    title: null
  });
  const retry = /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: () => setAttempt(n => n + 1)
  }, "Retry");
  return /*#__PURE__*/React.createElement("div", {
    className: rootClass,
    "data-testid": "portal-account"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    className: classes.intakeLabel
  }, "This utility's portal account"), status === 'loading' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Loading this utility's portal login\u2026"), status === 'planFailed' && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: retry
  }, "Couldn't load your company's plan, so this utility's portal login isn't shown yet."), status === 'failed' && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: retry
  }, "Couldn't load this utility's portal login."), status === 'ready' && !portal && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "Utility portal needs Autopilot. Livable doesn't fetch these bills on your plan."), credential ? section(false) : null), status === 'ready' && portal && /*#__PURE__*/React.createElement(React.Fragment, null, provider.isFetchable && (!credential || !credential.disabledOn) ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, portalCheckLine(provider, credential)) : /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, portalCheckLine(provider, credential)), credential !== null && credential !== void 0 && credential.needsAccountNumber ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, "This login has no account number yet. Add it with Update login, so Livable can match its bills to this utility.") : null, credential || provider.isFetchable ? section(true) : null));
});